import { t } from 'i18next';
import { FC, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { getErrorMessage } from '../../../../common/utils';
import { getTimestampFromAnyPlatform } from '../../../../common/videoUtils';
import { Alert } from '../../../components/alerts';
import { Form } from '../../../components/form/Form';
import { FormControl } from '../../../components/form/FormControl';
import { FormControlInput } from '../../../components/form/FormControlInput';
import { FormControlLabel } from '../../../components/form/FormControlLabel';
import { FormControlSwitch } from '../../../components/form/FormControlSwitch';
import { useForm } from '../../../components/form/useForm';
import Modal from '../../../components/modal/modal';
import { Button } from '../../../components/presentational/buttons/Button';
import { Stack } from '../../../components/stack/Stack';
import { formatMsShort } from '../../../libs/utils';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import POVSelect, { PovSelectOptionValue } from './POVSelect';

interface VideoModalProps {
  isOpen: boolean;
  type: string;
  onClose: () => void;
  validate: (url: string, withTimestamp: boolean, pov?: number) => string | null;
  onSubmit: (query: any) => Promise<void> | void;
  rosters: any[];
  forceRefresh: any;
}

interface IFormValues {
  videoUrl: string;
  withTimestamp: boolean;
  selectedRoster: PovSelectOptionValue;
  timestampSeconds: number;
}

const VideoModal: FC<VideoModalProps> = ({ type, isOpen, onClose, onSubmit, rosters, forceRefresh, validate }) => {
  const form = useForm<IFormValues>({});

  useEffect(() => {
    const timestamp = getTimestampFromAnyPlatform(form.values.videoUrl);
    form.onChangeValue('timestampSeconds', timestamp || 0);
  }, [form.values.videoUrl]);

  const {
    isLoading,
    error,
    exec: submitHandler,
    resetError: clearError
  } = useAsyncCallback(async (submittedValues: IFormValues) => {
    const query: Record<string, any> = {
      videoUrl: submittedValues.videoUrl,
      withTimestamp: submittedValues.withTimestamp
    };

    if (!submittedValues.selectedRoster) {
      throw new Error(t('videos:errors.select-point-of-view'));
    }

    if (submittedValues.selectedRoster !== 'multi-pov') {
      query.povCharacterId = submittedValues.selectedRoster;
    }

    const notValid = validate(submittedValues.videoUrl, submittedValues.withTimestamp);

    if (notValid) {
      throw new Error(notValid);
    }

    await onSubmit(query);

    forceRefresh();
    resetStateOnClose();
  });

  const resetStateOnClose = () => {
    form.resetValues();
    onClose();
  };

  return (
    <Modal
      className="background-common--important"
      classModalContent="raider-modal__content"
      isOpen={isOpen}
      onRequestClose={resetStateOnClose}
      title={t(`videos:modal.${type}-title`)}
    >
      <Form labelSize="medium" form={form} onSubmit={form.formSubmitHandler(submitHandler)}>
        <Modal.Body>
          {error && (
            <FormControl>
              <Alert
                message={t(getErrorMessage(error), { defaultValue: getErrorMessage(error) })}
                type="error"
                onClose={clearError}
              />
            </FormControl>
          )}

          <FormControl required>
            <FormControlLabel>{t('videos:modal.video-url-label')}</FormControlLabel>

            <FormControlInput
              type="url"
              placeholder={t('videos:modal.video-url-placeholder')}
              {...form.register('videoUrl')}
              autoComplete="off"
              variant="rio"
              required
            />
          </FormControl>

          {form.values.timestampSeconds > 0 && (
            <FormControl>
              <FormControlSwitch
                id="timestamp"
                {...form.register('withTimestamp', {
                  setValueAs: 'event-target-checked'
                })}
                inlineLabel={
                  <Trans
                    i18nKey="videos:start-video-at"
                    values={{
                      timestamp: formatMsShort(form.values.timestampSeconds * 1000)
                    }}
                  >
                    <b />
                  </Trans>
                }
              />
            </FormControl>
          )}

          <FormControl required>
            <FormControlLabel>{t('videos:modal.point-of-view-label')}</FormControlLabel>
            <POVSelect
              theme="rio"
              size="small"
              placeholder={t('videos:modal.select-point-of-view')}
              isSearchable={false}
              {...form.register('selectedRoster', {
                setValueAs: 'identity'
              })}
              options={rosters.map(({ character }) => character)}
            />
          </FormControl>
        </Modal.Body>

        <Modal.Footer>
          <Stack justifyContent="flex-end">
            <Button type="button" variant="gray" onClick={resetStateOnClose} disabled={isLoading}>
              {t('common:terms.close')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              isLoading={isLoading}
              disabled={!form.values.videoUrl || !form.values.selectedRoster}
            >
              {isLoading ? t('common:loading') : t('videos:add-video-button')}
            </Button>
          </Stack>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default VideoModal;
